/* Presentation component */
import * as Sentry from "@sentry/react";
import React, { useState, useEffect, useRef, startTransition } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "seed/gql";
import { usePost, usePut } from "seed/api";
import { Loading } from "seed/helpers";
import { IS_LITE } from "settings";
import { PERMISSIONS } from "components/util/permissions";
import { getDefaultSection } from "components/util/redirect_resolver";
import { useAuth } from "components/util/helpers/providers/AuthProvider";
import { usePermissions } from "components/util/helpers/providers/PermissionsProvider";
import { UnsavedChangesProvider, useUnsavedChanges } from "components/util/helpers/UnsavedChanges";
import { detectBrowserLanguage } from "components/util/l.lib";
import l from "components/util/l";
import View from "./Presentation.view";

function PresentationWrapper({ isEmbedded = false }) {
  return (
    <UnsavedChangesProvider>
      <Presentation isEmbedded={isEmbedded} />
    </UnsavedChangesProvider>
  );
}

function Presentation({ isEmbedded = false }) {

  const flowToken = localStorage.getItem("flow_token");

  const user = useAuth();
  const isDeveloper = user?.isDeveloper;

  const history = useHistory();
  const location = useLocation();
  const { initializePermissions, hasPermission, isAdmin, role: userRole, snapshot } = usePermissions();
  const { confirmBeforeLeaving, resetUnsavedChanges } = useUnsavedChanges();

  const params = new URLSearchParams(location.search);
  const execModeRef = useRef(null);

  const [flowId, setFlowId] = useState(null);
  const [isStartShown, setIsStartShown] = useState(false);
  const [activeSection, setActiveSection] = useState({});
  const [cacheViews, setCacheViews] = useState([]);
  const [isContactFormShown, setIsContactFormShown] = useState(false);
  const [isAssistantShown, setIsAssistantShown] = useState(false);

  const changeFlow = (flow, { fromRoute = false } = {}) => {

    if (flow.id == flowId) return;

    enterFlowRoute(flow, { fromRoute, isFlowSwitch: flowId != null });

    // Set flow id in states and localStorage
    setFlowId(flow.id);
    // `flow_id` is where the app remembers which project to reopen. A framed presentation
    // has no such thing — its flow comes from the URL on every load — and the storage it
    // would write belongs to the host page, whose own project this would replace the next
    // time that page loads.
    if (!isEmbedded) localStorage.setItem(`flow_id`, flow.id)
    // `flow_token` is deliberately NOT written here. It does not mean "the flow that is
    // open" — it means this session is pinned to one flow (client portal access), and the
    // header reads it to hide the flow switcher and the edit buttons. Only a token link
    // that went through login sets it; being framed says so with `isEmbedded` instead,
    // which dies with the render rather than with the browser profile.
    document.title = `${flow.name} - Workix`

    Sentry.setContext("flow", { id: flow.id, name: flow.name });
    setCacheViews([]);
    // Resolved once this flow's permissions arrive (see the effect below)
    setActiveSection({});

    // Disable debug mode in executions & Update language
    if (flow.settings?.default_debug_execution == true || (flow.presentationSettings?.language ?? "auto") == "auto")
      callSetFlow({
        id: flow.id,
        presentationSettings: {
          ...flow.presentationSettings,
          ...((flow.presentationSettings?.language ?? "auto") == "auto" ? { language: detectBrowserLanguage() } : {})
        },
        settings: {
          ...flow.settings,
          ...(flow.settings?.default_debug_execution == true ? { default_debug_execution: false } : {})
        }
      });

  }

  const flowsQuery = isDeveloper || isEmbedded
    ? "is_active=true"
    : "is_active=true AND is_internal=false";

  const reqFlow = useQuery(`{
    flows {
      name
      description
      token
      presentationSettings
      settings
      buildStatus
      buildSettings
      isReleased
      environment
      flowViews {
        id
        name
        isPrebuilt
        settings
        dist {
          url
        }
      }
      theme {
        logo
        logoStyle
        primaryColor
        accentColor        
      }
    }
  }`, flowsQuery, {
    onCompleted: (data) => {
      // Set default flow
      if (data.flows.length == 0) return

      // Everything under /app is the project app's route space, so the flow is named on
      // arrival and not on every URL: the first segment of a link like workix.mx/token is
      // read as a token here, and dropped from the path by changeFlow once it has done its
      // job. A segment that is nobody's token is simply a route of the app.
      let routeFlow = data.flows.find(flow => flow.token == getRouteToken(history.location.pathname))

      // A framed flow is addressed by the token in its own path and by nothing else. The
      // fallbacks below are the host session's memory of what *it* had open, and reaching
      // for them inside a frame paints the framing project a second time — which, when the
      // frame is the one that project opened, paints it again, and again, until the browser
      // refuses to nest any further. A token this session cannot open renders nothing.
      if (isEmbedded) {
        if (routeFlow) changeFlow(withFlowViews(routeFlow), { fromRoute: true })
        return
      }

      // Then by id (localstorage, param), and by default the first flow (-id)
      let defaultFlowId = localStorage.getItem(`flow_id`) || params.get("flow_id")
      let defaultFlow =
        routeFlow ||
        data.flows.find(flow => flow.id == defaultFlowId) ||
        data.flows[0]

      changeFlow(withFlowViews(defaultFlow), { fromRoute: Boolean(routeFlow) })
    },
    orderBy: "-id"
  });

  const flows = reqFlow.data?.flows?.map(withFlowViews);
  const flow = (flows ?? []).find(flow => flow.id == flowId);

  const [callSetFlow] = usePut("/flows");

  const [callSaveViews] = usePost("/flows/save_views", {
    onCompleted: () =>
      reqFlow.refetch()
  });

  const [callSetPrompt] = usePost("/flows/submit_brief_contact", {
    onCompleted: () => {
      reqFlow.refetch();
      setIsContactFormShown(false);
    }
  });

  useEffect(() => {
    if (flowId)
      initializePermissions({ flowId });
  }, [flowId]);

  // The landing section depends on what the role can open, so it waits for the snapshot:
  // resolved earlier, a role without "Solución" landed on it with no tab to leave by.
  // The provider outlives this route, so a remount finds the previous screen's snapshot
  // before the flows have loaded; that one is skipped and the flow's own arrives later.
  useEffect(() => {
    if (!snapshot || !flow) return;
    const defaultSection = getDefaultSection({
      flow,
      buildStatus: getBuildStatus(flow, flows),
      isDeveloper,
      hasPermission,
    });
    sessionStorage.setItem(`active_section_${flow.id}`, defaultSection.name);
    setActiveSection(defaultSection);
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Update flow when URL param changes
    const flowIdParam = params.get("flow_id")
    const flow = (flows ?? []).find(flow => flow.id == flowIdParam);
    if (flow && flowId != flowIdParam) {
      changeFlow(flow)
      closeStart()
    }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFlow = () =>
    reqFlow.refetch();

  const refreshChat = () =>
    setCacheViews(views => views.filter(view => view.name !== "agent"));

  const closeStart = () => setIsStartShown(false)

  // What the URL does when a flow opens. The path under /app belongs to the project's app
  // (see Main.js), so the rule is to touch it as little as possible:
  //
  //   named by the path   the token has been consumed, so it leaves — /acme/reports/12
  //                       becomes /reports/12 and the app opens on that screen
  //   switching flows     the path of the previous project means nothing to the incoming
  //                       one, so the app starts at its root
  //   anything else       left exactly as it arrived: it is a route of the app
  const enterFlowRoute = (flow, { fromRoute, isFlowSwitch }) => {

    const { pathname, search } = history.location;

    if (fromRoute) {
      const rest = pathname.slice(`/${flow.token}`.length);
      history.replace({ pathname: rest || "/", search });
      return;
    }

    if (isFlowSwitch)
      history.replace({ pathname: "/", search });

  }

  const getRouteToken = (pathname) => pathname.split("/")[1] || "";

  const changeActiveSection = (name, args = {}) => {

    // Sections are UI state, not routes. The URL under /app belongs to the project's app
    // (see Main.js), so switching tabs leaves it alone and coming back to "main" finds the
    // app exactly where it was.
    sessionStorage.setItem(`active_section_${flow.id}`, name)
    startTransition(() => {
      setActiveSection({ name, ...args });
    });

    setIsStartShown(false);

  }

  const onClickTab = (name, args = {}) => {
    if (activeSection.name !== name)
      return confirmBeforeLeaving(() => {
        resetUnsavedChanges();
        changeActiveSection(name, args);
      });
    changeActiveSection(name, args);
  }

  const onChangeFlow = (flowId) => {

    const changeSelectedFlow = () => {
      const flow = (flows ?? []).find(flow => flow.id == flowId);
      setIsStartShown(false)
      changeFlow(flow)
    };

    return confirmBeforeLeaving(() => {
      resetUnsavedChanges();
      changeSelectedFlow();
    });
    
  }

  const onClickPinView = (viewName) => {
    const views = (flow.presentationSettings?.views || []).map(view =>
      view.name == viewName
        ? { ...view, is_pinned: !view.is_pinned }
        : view
    );
    const presentationSettings = { ...flow.presentationSettings };
    delete presentationSettings.views;

    callSaveViews({
      flow_id: flow.id,
      views,
      presentation_settings: presentationSettings,
    });
    onClickTab(viewName);
  }

  const onClickStartButton = () =>
    setIsStartShown(!isStartShown)

  const onClickShowContactUs = () =>
    setIsContactFormShown(true);

  const onClickHideContactForm = () =>
    setIsContactFormShown(false);

  const onClickShowAssistant = () =>
    setIsAssistantShown(true);

  const onClickHideAssistant = () =>
    setIsAssistantShown(false);

  const onSubmitForm = (values) => {
    callSetPrompt({
      flow_id: flowId,
      submission: values,
    });
  }

  if (reqFlow.loading) return <PlaceholderView />;
  if (reqFlow.error) return "";

  // If there are no flows, redirect to get started; lite creates none, its login syncs the first one
  if ((flows ?? []).length == 0 && !isEmbedded)
    return window.location.href = IS_LITE ? "/login?sync" : "/get_started";

  // Framed, and the token in the path names nothing this session can open — the seat on
  // that flow was never granted to this account. There is no second flow to try, so this
  // says so instead of spinning forever.
  if (!flow && isEmbedded) return <UnavailableView />;

  // The active flow is resolved from flowId, which is only set once the flows
  // request completes and changeFlow runs.
  if (!flow) return <PlaceholderView />;

  if (!userRole && !isAdmin) return <PlaceholderView />;

  if (!activeSection.name) return <PlaceholderView />;

  const presentationSettings = flow.presentationSettings || {};
  const buildStatus = getBuildStatus(flow, flows);

  const showManageCollections = hasPermission(PERMISSIONS.SHOW_MANAGE_COLLECTIONS);

  // Show topnav if user has permission, is developer, or if flow is not built and the user has
  // an account: the portal is where onboarding ends, and without the bar a person with a single
  // project had no way out to the solutions list. An anonymous visitor has nothing to go back to.
  // Lite ships no topnav (bin/dist/.distignore), so it must not keep the room for one either.
  const showTopnav = !IS_LITE && (hasPermission(PERMISSIONS.SHOW_TOPNAV) || isDeveloper ||
    (buildStatus != "BUILT" && buildStatus != "TEMPLATE_STARTED" && !user?.isAnonymous));
  
  return <View
    user={user}
    flow={flow}
    isEmbedded={isEmbedded}
    isPinnedToFlow={isEmbedded || flowToken == flow.token}
    buildStatus={buildStatus}
    cacheViews={cacheViews}
    showManageCollections={showManageCollections}
    showTopnav={showTopnav}
    presentationSettings={presentationSettings}
    activeSection={activeSection}
    execModeRef={execModeRef}
    userRole={userRole}
    isAdmin={isAdmin}
    isDeveloper={isDeveloper}
    isStartShown={isStartShown}
    isContactFormShown={isContactFormShown}
    isAssistantShown={isAssistantShown}
    refreshFlow={refreshFlow}
    refreshChat={refreshChat}
    closeStart={closeStart}
    setCacheViews={setCacheViews}
    onClickStartButton={onClickStartButton}
    onClickTab={onClickTab}
    onChangeFlow={onChangeFlow}
    onClickPinView={onClickPinView}
    onClickShowContactUs={onClickShowContactUs}
    onClickHideContactForm={onClickHideContactForm}
    onClickShowAssistant={onClickShowAssistant}
    onClickHideAssistant={onClickHideAssistant}
    onSubmitForm={onSubmitForm}
  />;

}

const PlaceholderView = () =>
  <div className="vw-100 vh-100">
    <Loading />
  </div>

const UnavailableView = () =>
  <div className="vw-100 vh-100 d-flex justify-content-center align-items-center px-4">
    <p className="text-muted mb-0 text-center">
      {l("El asistente no está disponible por ahora%PT001")}
    </p>
  </div>

// The build status is the project's client-facing state, and the project is its PROD flow:
// it decides whether the client sees the portal or the app. Every environment is cloned
// carrying a copy of that status, so reading it on a working branch puts a collaborator
// invited to DEV in the client portal of a project that was built months ago.
const getBuildStatus = (flow, flows) => {
  if (flow.environment != "PROD") return "BUILT";
  // The onboarding assistant is how an account asks for its first solution. Whoever can
  // already open another one is past it, and works this project through their role.
  if (flow.buildStatus == "STARTED" && flows.length > 1) return "BUILT";
  return flow.buildStatus || "NONE";
}

const withFlowViews = (flow) => ({
  ...flow,
  presentationSettings: {
    ...(flow.presentationSettings || {}),
    views: (flow.flowViews || []).map(view => ({
      ...(view.settings || {}),
      id: view.id,
      name: view.name,
      is_prebuilt: view.isPrebuilt,
      url: view.dist?.url,
    })),
  },
});

export default PresentationWrapper;
